import { useMemo } from 'react';

import {
  getFilteredMeterChartDataByVisibleKeys,
  getFilteredTradeChartDataByVisibleKeys,
} from 'src/util/chartFiltering';
import { DEFAULT_MAX_VISIBLE_CARDS_COUNT } from 'src/util/constants';

/**
 * Computes visible cards, the visible key set, and filtered chart data for
 * paginated dashboard charts. Handles pinning of selected-but-off-page cards
 * so they remain visible in their own dedicated series rather than being
 * merged into the "Other" bucket.
 *
 * Shared between PortfolioShowChart and PropertyShowChart. Any change to the
 * per-point data-shape contract in chartFiltering.js must be reflected in
 * both callers.
 * @param {object} params
 * @param {Array} params.pageCards - Cards for the current page from buildPaginationMetadata.
 * @param {string[]} params.finalSelectedKeys - Selected card keys for the active chart view.
 * @param {number} params.totalPages - Total page count from buildPaginationMetadata.
 * @param {Array} params.cardsData - Full list of all cards across all pages.
 * @param {object} params.chartData - Raw chart data (trade or meter shape).
 * @param {boolean} params.meterView - Whether the active source is meter/historian view.
 * @param {string} params.otherLabel - Translated label for the aggregated "Other" series.
 * @returns {{ visibleCards: Array, visibleKeys: Set<string>|null, filteredChartData: object }}
 * Filtered cards and chart data for the current page.
 */
const useFilteredChartData = ({
  pageCards,
  finalSelectedKeys,
  totalPages,
  cardsData,
  chartData,
  meterView,
  otherLabel,
}) => {
  // When on a page that does not natively contain the selected card, prepend it so it
  // remains visible and retains its own dedicated bar (not merged into "Other").
  const visibleCards = useMemo(() => {
    if (finalSelectedKeys.length === 0 || totalPages <= 1) {
      return pageCards;
    }
    const pageKeySet = new Set(pageCards.map((c) => c.key));
    const selectedKeySet = new Set(finalSelectedKeys);
    const pinnedCards = cardsData.filter(
      (c) => selectedKeySet.has(c.key) && !pageKeySet.has(c.key),
    );
    // Cap the combined set to DEFAULT_MAX_VISIBLE_CARDS_COUNT so the chart
    // never renders more series than the page size, regardless of how many
    // selected-but-off-page cards exist.
    const combined = pinnedCards.length > 0 ? [...pinnedCards, ...pageCards] : pageCards;
    return combined.slice(0, DEFAULT_MAX_VISIBLE_CARDS_COUNT);
  }, [pageCards, finalSelectedKeys, totalPages, cardsData]);

  const visibleKeys = useMemo(
    () => (totalPages > 1 ? new Set(visibleCards.map((c) => c.key)) : null),
    [totalPages, visibleCards],
  );

  const filteredChartData = useMemo(() => {
    if (!visibleKeys) {
      return chartData;
    }
    if (meterView) {
      return getFilteredMeterChartDataByVisibleKeys(chartData, visibleKeys, otherLabel);
    }
    return getFilteredTradeChartDataByVisibleKeys(chartData, visibleKeys, otherLabel);
  }, [chartData, meterView, visibleKeys, otherLabel]);

  return { visibleCards, visibleKeys, filteredChartData };
};

export default useFilteredChartData;
